import React from "react";
import { Star } from "lucide-react";
import { useSection } from "@/context/SiteContentContext";

/**
 * Studio Trustpilot strip — lives just under the announcement bar on every page.
 *
 * Rating + review-count + URLs are admin-editable via Admin → Site Content
 * (slug=trustpilot). Defaults are conservative; bump them as real reviews land.
 * One CTA links straight to the Trustpilot evaluate page; the rating itself
 * links to the public profile.
 */

const DEFAULTS = {
  enabled: true,
  rating: 4.8,
  review_count: 0,
  evaluate_url: "https://uk.trustpilot.com/evaluate/motowear.co.uk",
  profile_url: "https://uk.trustpilot.com/review/motowear.co.uk",
  blurb: "Loved the studio? Leave us a review on Trustpilot — keeps the lights on.",
};

export default function TrustpilotStrip() {
  const section = useSection("trustpilot") || {};
  const meta = section.meta || {};
  // The seeded shape uses manual_score / manual_count. Map gracefully so the
  // admin can override with explicit evaluate_url / profile_url / blurb / enabled.
  const data = {
    ...DEFAULTS,
    rating: Number(meta.manual_score ?? meta.rating ?? DEFAULTS.rating) || DEFAULTS.rating,
    review_count: Number(meta.manual_count ?? meta.review_count ?? 0) || 0,
    evaluate_url: meta.evaluate_url || meta.review_link || DEFAULTS.evaluate_url,
    profile_url: meta.profile_url || (meta.domain ? `https://uk.trustpilot.com/review/${meta.domain}` : DEFAULTS.profile_url),
    blurb: meta.blurb || DEFAULTS.blurb,
    enabled: meta.enabled === false ? false : true,
  };

  if (!data.enabled) return null;
  const rating = Number(data.rating || 0);
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;

  return (
    <div
      className="bg-white border-b border-black/10 text-[11px] md:text-[12px]"
      data-testid="trustpilot-strip"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-12 py-2 flex items-center gap-4 justify-center md:justify-between flex-wrap">
        <a
          href={data.profile_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 group"
          data-testid="trustpilot-rating-link"
          aria-label={`MotoWear scores ${rating.toFixed(1)} out of 5 on Trustpilot`}
        >
          <span className="label-eyebrow text-black/60 text-[10px]">Trustpilot</span>
          <span className="flex items-center" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((i) => {
              const filled = i < full;
              const isHalf = !filled && i === full && half;
              return (
                <span key={i} className="relative inline-block" style={{ width: 16, height: 16 }}>
                  <Star size={16} strokeWidth={1.5} className="text-black/15" />
                  {(filled || isHalf) && (
                    <span
                      className="absolute inset-0 overflow-hidden"
                      style={{ width: isHalf ? "50%" : "100%" }}
                    >
                      <Star size={16} strokeWidth={1.5} className="text-[#00B67A] fill-[#00B67A]" />
                    </span>
                  )}
                </span>
              );
            })}
          </span>
          <span className="font-semibold text-black">{rating.toFixed(1)}</span>
          {data.review_count > 0 && (
            <span className="text-black/40">· {data.review_count} reviews</span>
          )}
        </a>

        <span className="hidden md:inline text-black/55 italic truncate" data-testid="trustpilot-blurb">
          {data.blurb}
        </span>

        <a
          href={data.evaluate_url}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-[#00B67A] text-white px-4 py-1.5 label-eyebrow text-[10px] hover:bg-[#0a0a0a] transition-colors"
          data-testid="trustpilot-leave-review"
        >
          Leave a review
        </a>
      </div>
    </div>
  );
}
